import React, { useState } from 'react';
import { Trophy, Lock } from 'lucide-react';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';

// Each achievement is defined by a real, checkable criterion instead of a
// fixed unlocked/progress value, so it always reflects what the person has
// actually done.
//
// Streak-based achievements unlock permanently once bestStreak reaches the
// target (earning a trophy once means keeping it, even after a harder
// stretch later) but the progress bar for a still-locked one shows the
// *current* streak, since that's the honest answer to "how close am I
// right now".
const ACHIEVEMENT_DEFS = [
  { id: 'first_day', name: 'הצעד הראשון', desc: 'יום נקי ראשון', icon: '🎯', rarity: 'common', metric: 'bestStreak', target: 1 },
  { id: 'week', name: 'שבוע זהב', desc: '7 ימים ברציפות', icon: '⭐', rarity: 'common', metric: 'bestStreak', target: 7 },
  { id: 'month', name: 'חודש הכר', desc: '30 ימים ברציפות', icon: '🏆', rarity: 'rare', metric: 'bestStreak', target: 30 },
  { id: 'day_50', name: 'חצי מאה', desc: '50 ימים ברציפות', icon: '👑', rarity: 'rare', metric: 'bestStreak', target: 50 },
  { id: 'day_100', name: 'מאה ימים', desc: '100 ימים ברציפות', icon: '💯', rarity: 'epic', metric: 'bestStreak', target: 100 },
  { id: 'check_50', name: 'בדיקה עקבית', desc: '50 בדיקות יומיות', icon: '✅', rarity: 'common', metric: 'totalDays', target: 50 },
  { id: 'moods_50', name: 'לב פתוח', desc: '50 רגשות חיוביים מתועדים', icon: '❤️', rarity: 'rare', metric: 'positiveMoods', target: 50 },
  { id: 'year', name: 'שנה שלמה', desc: '365 ימים ברציפות', icon: '🌟', rarity: 'legendary', metric: 'bestStreak', target: 365 },
];

const RARITY_STYLES = {
  common: { bg: 'from-stone-400 to-stone-600', label: 'רגיל' },
  rare: { bg: 'from-brand-400 to-brand-600', label: 'נדיר' },
  epic: { bg: 'from-bloom-400 to-bloom-600', label: 'אפי' },
  legendary: { bg: 'from-gold-400 to-gold-600', label: 'אגדי' },
};

const AchievementsScreen = () => {
  const { userData, dailyLogs } = useApp();
  const [filter, setFilter] = useState('all');

  const positiveMoods = Object.values(dailyLogs || {}).filter(
    l => l.mood === 'excellent' || l.mood === 'good'
  ).length;

  const achievedValues = { bestStreak: userData.bestStreak, totalDays: userData.totalDays, positiveMoods };
  // What counts as "current progress" toward a still-locked achievement -
  // for streak goals this is the live streak, not the best-ever one.
  const progressValues = { bestStreak: userData.streak, totalDays: userData.totalDays, positiveMoods };

  const ACHIEVEMENTS = ACHIEVEMENT_DEFS.map(def => {
    const unlocked = achievedValues[def.metric] >= def.target;
    const progress = unlocked ? 100 : Math.min(99, Math.round((progressValues[def.metric] / def.target) * 100));
    return { ...def, unlocked, progress };
  });

  const filtered = ACHIEVEMENTS.filter(a => {
    if (filter === 'unlocked') return a.unlocked;
    if (filter === 'locked') return !a.unlocked;
    return true;
  });
  const unlockedCount = ACHIEVEMENTS.filter(a => a.unlocked).length;

  return (
    <div dir="rtl" className="min-h-screen bg-stone-50 pb-24">
      <Header title="הישגים" subtitle={`${unlockedCount}/${ACHIEVEMENTS.length} הושגו`} />

      <div className="px-4 py-4 space-y-4">
        {/* Overall Progress */}
        <div className="bg-white rounded-xl p-4 shadow">
          <div className="flex justify-between mb-2">
            <span className="font-semibold text-stone-800">התקדמות כללית</span>
            <span className="font-bold text-brand-600">{Math.round((unlockedCount / ACHIEVEMENTS.length) * 100)}%</span>
          </div>
          <div className="w-full bg-stone-200 rounded-full h-4 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand-400 to-brand-600"
              style={{ width: `${(unlockedCount / ACHIEVEMENTS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl p-3 shadow flex gap-2">
          {['all', 'unlocked', 'locked'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-all ${
                filter === f ? 'bg-brand-500 text-white' : 'bg-stone-100 text-stone-700'
              }`}
            >
              {f === 'all' ? 'הכל' : f === 'unlocked' ? 'פתוח' : 'נעול'}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 gap-3">
          {filtered.map(a => {
            const { bg, label } = RARITY_STYLES[a.rarity];
            return (
              <div
                key={a.id}
                className={`rounded-xl p-4 text-center ${
                  a.unlocked
                    ? `bg-gradient-to-br ${bg} text-white shadow-lg`
                    : 'bg-white border border-stone-200 opacity-70'
                }`}
              >
                <div className="flex justify-center mb-1">
                  {a.unlocked
                    ? <Trophy className="w-4 h-4 text-white opacity-80" />
                    : <Lock className="w-4 h-4 text-stone-400" />
                  }
                </div>
                <div className="text-4xl mb-2">{a.icon}</div>
                <h3 className={`font-bold text-sm mb-1 ${a.unlocked ? 'text-white' : 'text-stone-800'}`}>{a.name}</h3>
                <p className={`text-xs mb-2 ${a.unlocked ? 'text-white opacity-80' : 'text-stone-500'}`}>{a.desc}</p>

                {!a.unlocked && (
                  <div>
                    <div className="w-full bg-stone-200 rounded-full h-1.5 overflow-hidden mb-1">
                      <div className="h-full bg-brand-400" style={{ width: `${a.progress}%` }} />
                    </div>
                    <p className="text-xs text-stone-500">{a.progress}%</p>
                  </div>
                )}

                <p className={`text-xs font-semibold mt-1 ${a.unlocked ? 'text-white opacity-70' : 'text-stone-400'}`}>
                  {label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AchievementsScreen;
